/**
 * URL query parameter names for marketing click IDs (ad attribution).
 *
 * `gclid` and `fbclid` are deliberately NOT here (CONF-03): the properties of
 * record for those platforms — `ga_aw_clid` and `fb_fbc` — are read straight
 * off the `_gcl_aw` / `_fbc` cookies Google's and Meta's own scripts write
 * (see `google-and-facebook-properties`), independent of this capture system.
 * Capturing the raw params here only reproduced them verbatim as event
 * properties, which Segment Protocols flags as an undeclared property.
 */
export const MARKETING_CLICK_ID_PARAMS = [
  'awc',
  'ttclid',
  'msclkid',
  'sccid',
  'dclid',
  'twclid',
  'li_fat_id',
  'epik',
  'rdt_cid',
] as const

export type MarketingClickIdParam = (typeof MARKETING_CLICK_ID_PARAMS)[number]

/** Map of marketing click ID param name to value (for event properties). */
export type MarketingClickIds = Partial<Record<MarketingClickIdParam, string>>

/**
 * Platform cookie fallbacks for click IDs (set by ad platform SDKs).
 * Key: URL param name; value: cookie name to read.
 */
export const MARKETING_CLICK_ID_COOKIE_FALLBACK: Partial<
  Record<MarketingClickIdParam, string>
> = {
  dclid: '_gcl_dc',
  ttclid: '_ttp',
  msclkid: '_uetmsclkid',
  li_fat_id: 'li_fat_id',
  // Set by Pinterest's own tag from the epik URL param. Confirmed live on
  // testing.suitsupply.com (2026-09-10) — not "epik" as previously documented.
  epik: '_epik',
  // Set by Reddit's own pixel from the rdt_cid URL param. Confirmed live on
  // testing.suitsupply.com (2026-09-10) — not "_rdt_uuid" (that cookie is
  // Reddit's unrelated persistent identity id, not the click id).
  rdt_cid: '_rdt_cid',
}

/** URL param aliases (e.g. Snapchat uses both sccid and ScCid). */
export const URL_PARAM_ALIASES: Partial<
  Record<MarketingClickIdParam, readonly string[]>
> = {
  sccid: ['sccid', 'ScCid'],
}

/**
 * Legacy localStorage key — kept only so it can be migrated into the cookie
 * and deleted (FC-183 D17). Do not write to it.
 */
export const STORAGE_KEY_MARKETING_CLICK_IDS = 'suitsupply_marketing_click_ids'

/**
 * Cookie persisting captured click IDs across subdomains (O6). localStorage is
 * origin-scoped and broke attribution on every subdomain hop; the cookie is
 * scoped to .suitsupply.com with a FIXED expiry set when a click ID is
 * captured — never extended by mere visits (the old rolling TTL was a
 * last-touch decision nobody made).
 */
export const MARKETING_CLICK_IDS_COOKIE_NAME = 'suitsupply_marketing_click_ids'

/**
 * How long a captured click ID is kept (days). Fixed from the moment of
 * capture. Attribution model: last-touch — a NEW click ID overwrites the old
 * one and restarts this window (pre-existing de-facto behaviour, kept until
 * the FC-187 owner decides otherwise). Note Safari ITP caps script-written
 * cookies at 7 days regardless of this value.
 */
export const MARKETING_CLICK_IDS_EXPIRY_DAYS = 30

export interface StoredMarketingClickIds {
  clickIds: MarketingClickIds
  expiresAt: number
}
